import React, { useEffect, useMemo, useState } from 'react';
import { Button, Table, Select, Space, DatePicker, message } from 'antd';
import {
  SearchOutlined,
  FileExcelOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import locale from 'antd/es/date-picker/locale/vi_VN';
import {
  statisticsService,
  computeDateRange,
  type DatePreset,
} from '../../services';
import { doctorService } from '../../services';
import { mockDoctors } from '../../services/mock/doctors';
import { StatisticsInvoiceRow } from '../../types';
import { formatAmountDots } from '../../utils/format';
import InvoiceDetailModal from '../../components/InvoiceDetailModal';
import './StatisticsPage.css';

const SERVICE_OPTIONS = [
  { value: '', label: 'Tất cả dịch vụ' },
  { value: 'Xuất hóa đơn', label: 'Xuất hóa đơn' },
  { value: 'Khám bệnh', label: 'Khám bệnh' },
];

interface FiltersState {
  preset: DatePreset;
  customRange: [Dayjs | null, Dayjs | null] | null;
  service: string | undefined;
  doctorId: number | undefined;
}

const defaultFilters: FiltersState = {
  preset: 'today',
  customRange: null,
  service: undefined,
  doctorId: undefined,
};

function exportRowsToExcel(rows: StatisticsInvoiceRow[]) {
  const headers = [
    'STT',
    'Số hóa đơn',
    'Tên khách hàng',
    'Dịch vụ',
    'Ghi chú',
    'Ngày tạo đơn',
    'Ngày gửi đơn',
    'Tổng tiền',
  ];
  const lines = [headers.join(',')];
  rows.forEach((r, i) => {
    const esc = (s: string) => `"${String(s).replace(/"/g, '""')}"`;
    lines.push(
      [
        i + 1,
        `#${r.invoice_number}`,
        r.customer_name,
        r.service_name,
        r.note,
        formatDateTime(r.created_at),
        r.sent_at ? formatDateTime(r.sent_at) : '',
        formatAmountDots(parseFloat(r.total_amount) || 0),
      ]
        .map((c) => esc(String(c)))
        .join(',')
    );
  });
  const bom = '\uFEFF';
  const blob = new Blob([bom + lines.join('\n')], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `thong-ke-hoa-don-${dayjs().format('YYYYMMDD-HHmm')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
  message.success('Đã xuất file');
}

function formatDateTime(iso: string): string {
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD/MM/YYYY HH:mm') : '—';
}

const StatisticsPage: React.FC = () => {
  const [draft, setDraft] = useState<FiltersState>(defaultFilters);
  const [applied, setApplied] = useState<FiltersState>(defaultFilters);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailInvoiceId, setDetailInvoiceId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const { data: doctorsFromApi = [] } = useQuery({
    queryKey: ['doctors-list-statistics'],
    queryFn: () => doctorService.getList(),
  });

  const doctors = doctorsFromApi.length > 0 ? doctorsFromApi : mockDoctors;

  const { data: statsResponse, isLoading } = useQuery({
    queryKey: ['statistics', applied],
    queryFn: async () => {
      const { startDate, endDate } = computeDateRange(applied.preset, applied.customRange);
      return statisticsService.getRows({
        startDate,
        endDate,
        service_name: applied.service || undefined,
        doctor_id: applied.doctorId,
      });
    },
  });

  const rows = statsResponse?.data ?? [];

  useEffect(() => {
    setCurrentPage(1);
  }, [applied]);

  const setPreset = (preset: DatePreset) => {
    setDraft((d) => ({
      ...d,
      preset,
      customRange: preset === 'custom' ? d.customRange ?? [null, null] : null,
    }));
  };

  const onSearch = () => {
    if (draft.preset === 'custom' && (!draft.customRange?.[0] || !draft.customRange?.[1])) {
      message.warning('Vui lòng chọn khoảng ngày khi dùng Tùy chọn');
      return;
    }
    setApplied({ ...draft });
  };

  const onExportExcel = () => {
    if (!rows.length) {
      message.warning('Không có dữ liệu để xuất');
      return;
    }
    exportRowsToExcel(rows);
  };

  const columns = useMemo(
    () => [
      {
        title: 'STT',
        key: 'stt',
        width: 64,
        align: 'center' as const,
        render: (_: unknown, __: StatisticsInvoiceRow, index: number) =>
          (currentPage - 1) * pageSize + index + 1,
      },
      {
        title: 'Số hóa đơn',
        dataIndex: 'invoice_number',
        key: 'invoice_number',
        render: (num: string, record: StatisticsInvoiceRow) => (
          <Button
            type="link"
            className="invoice-link"
            onClick={() => {
              setDetailInvoiceId(record.invoice_id);
              setDetailOpen(true);
            }}
          >
            #{num}
          </Button>
        ),
      },
      {
        title: 'Tên khách hàng',
        dataIndex: 'customer_name',
        key: 'customer_name',
        ellipsis: true,
      },
      {
        title: 'Dịch vụ',
        dataIndex: 'service_name',
        key: 'service_name',
        ellipsis: true,
      },
      {
        title: 'Ghi chú',
        dataIndex: 'note',
        key: 'note',
        ellipsis: true,
      },
      {
        title: 'Ngày tạo đơn',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 160,
        render: (v: string) => formatDateTime(v),
      },
      {
        title: 'Ngày gửi đơn',
        dataIndex: 'sent_at',
        key: 'sent_at',
        width: 160,
        render: (v: string | null) => (v ? formatDateTime(v) : '—'),
      },
      {
        title: 'Tổng tiền',
        dataIndex: 'total_amount',
        key: 'total_amount',
        width: 120,
        align: 'right' as const,
        render: (v: string) => formatAmountDots(parseFloat(v) || 0),
      },
    ],
    [currentPage, pageSize]
  );

  const datePresetButtons: { key: DatePreset; label: string; icon?: boolean }[] = [
    { key: 'today', label: 'Hôm nay' },
    { key: 'week', label: 'Tuần này' },
    { key: 'month', label: 'Tháng này' },
    { key: 'quarter', label: 'Quý này' },
    { key: 'year', label: 'Năm nay' },
    { key: 'custom', label: 'Tùy chọn', icon: true },
  ];

  return (
    <div className="statistics-page">
      <h1 className="statistics-page__title">Thống kê</h1>

      <div className="statistics-page__toolbar">
        <div className="statistics-page__toolbar-left">
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Space wrap className="statistics-page__date-buttons">
              {datePresetButtons.map((b) => (
                <Button
                  key={b.key}
                  type={draft.preset === b.key ? 'primary' : 'default'}
                  onClick={() => setPreset(b.key)}
                  icon={b.icon ? <CalendarOutlined /> : undefined}
                >
                  {b.label}
                </Button>
              ))}
            </Space>
            {draft.preset === 'custom' && (
              <DatePicker.RangePicker
                className="statistics-page__custom-range"
                value={draft.customRange ?? [null, null]}
                onChange={(range) =>
                  setDraft((d) => ({
                    ...d,
                    customRange: range as [Dayjs | null, Dayjs | null] | null,
                  }))
                }
                format="DD/MM/YYYY"
                locale={locale}
                placeholder={['Từ ngày', 'Đến ngày']}
              />
            )}
            <Space wrap align="center">
              <Select
                style={{ minWidth: 200 }}
                value={draft.service ?? ''}
                onChange={(v) =>
                  setDraft((d) => ({
                    ...d,
                    service: v === '' ? undefined : String(v),
                  }))
                }
                options={SERVICE_OPTIONS}
                optionFilterProp="label"
              />
              <Select
                style={{ minWidth: 200 }}
                placeholder="Chọn bác sĩ"
                allowClear
                value={draft.doctorId}
                onChange={(v) => setDraft((d) => ({ ...d, doctorId: v ?? undefined }))}
                options={doctors
                  .filter((doc) => doc.id != null)
                  .map((doc) => ({
                    value: doc.id as number,
                    label: doc.name,
                  }))}
              />
              <Button type="primary" icon={<SearchOutlined />} onClick={onSearch}>
                Tìm kiếm
              </Button>
            </Space>
          </Space>
        </div>
        <Button
          type="primary"
          className="statistics-page__btn-export"
          icon={<FileExcelOutlined />}
          onClick={onExportExcel}
        >
          Xuất excel
        </Button>
      </div>

      <Table<StatisticsInvoiceRow>
        rowKey="id"
        columns={columns}
        dataSource={rows}
        loading={isLoading}
        pagination={{
          current: currentPage,
          pageSize,
          showSizeChanger: true,
          showTotal: (t) => `Tổng ${t} bản ghi`,
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size || 20);
          },
        }}
        scroll={{ x: 1100 }}
      />

      <InvoiceDetailModal
        visible={detailOpen}
        invoiceId={detailInvoiceId}
        onClose={() => {
          setDetailOpen(false);
          setDetailInvoiceId(null);
        }}
      />
    </div>
  );
};

export default StatisticsPage;
